import { appLocalStore, ConsoleLogType, ConsoleMessage } from '../stores/appLocalStore';

/**
 * Console capture service that overrides native console methods
 * to capture all console output for display in the Console Panel
 */
class ConsoleCaptureService {
    private originalConsole: {
        log: typeof console.log;
        info: typeof console.info;
        warn: typeof console.warn;
        error: typeof console.error;
        debug: typeof console.debug;
    };

    private messageIdCounter = 0;

    constructor() {
        // Store original console methods
        this.originalConsole = {
            log: console.log.bind(console),
            info: console.info.bind(console),
            warn: console.warn.bind(console),
            error: console.error.bind(console),
            debug: console.debug.bind(console),
        };
    }

    /**
     * Initialize console capture by overriding console methods
     */
    initialize() {
        this.overrideConsole('log');
        this.overrideConsole('info');
        this.overrideConsole('warn');
        this.overrideConsole('error');
        this.overrideConsole('debug');
    }

    /**
     * Override a specific console method
     */
    private overrideConsole(type: ConsoleLogType) {
        const original = this.originalConsole[type];
        
        (console as any)[type] = (...args: any[]) => {
            // Call original console method
            original(...args);

            // Capture the message asynchronously to avoid state updates during render
            setTimeout(() => {
                const message: ConsoleMessage = {
                    id: `console-${this.messageIdCounter++}`,
                    type,
                    args,
                    timestamp: new Date(),
                };

                appLocalStore.debug.console.addMessageAction(message);
            }, 0);
        };
    }

    /**
     * Restore original console methods (useful for cleanup)
     */
    restore() {
        console.log = this.originalConsole.log;
        console.info = this.originalConsole.info;
        console.warn = this.originalConsole.warn;
        console.error = this.originalConsole.error;
        console.debug = this.originalConsole.debug;
    }
}

export const consoleCaptureService = new ConsoleCaptureService();
